import { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from "recharts";
import { api } from "@/lib/api";

interface WorkReport {
  id: string;
  work_type: string;
  worker_count: number;
  work_hours: number;
  region: string;
  work_date: string;
}

const PIE_COLORS = ["#f59e0b", "#f97316", "#84cc16", "#10b981", "#14b8a6", "#06b6d4", "#6366f1", "#ec4899", "#6b7289"];
const CHART_COLORS = ["#f59e0b", "#10b981", "#f97316", "#06b6d4"];

function getPeriodRange(period: "week" | "month" | "year", selectedYear?: number) {
  const now = new Date();
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());

  if (period === "week") {
    const start = new Date(today);
    start.setDate(today.getDate() - 6);
    return {
      start: start.toISOString().slice(0, 10),
      end: today.toISOString().slice(0, 10),
    };
  }

  if (period === "month") {
    return {
      start: `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}-01`,
      end: today.toISOString().slice(0, 10),
    };
  }

  const year = selectedYear || now.getFullYear();
  const isCurrentYear = year === now.getFullYear();
  return {
    start: `${year}-01-01`,
    end: isCurrentYear ? today.toISOString().slice(0, 10) : `${year}-12-31`,
  };
}

export default function StatisticsPage() {
  const navigate = useNavigate();
  const [period, setPeriod] = useState<"week" | "month" | "year">("month");
  const [selectedYear, setSelectedYear] = useState<number>(new Date().getFullYear());
  const [reports, setReports] = useState<WorkReport[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    async function fetchReports() {
      setLoading(true);
      setError("");
      try {
        const data = await api.get<WorkReport[]>("/reports");
        setReports(data);
      } catch {
        setError("통계 데이터를 불러오는 중 오류가 발생했습니다.");
      } finally {
        setLoading(false);
      }
    }

    fetchReports();
  }, []);

  const availableYears = useMemo(() => {
    const years = new Set<number>();
    reports.forEach(r => {
      if (r.work_date) {
        const y = parseInt(r.work_date.slice(0, 4), 10);
        if (!isNaN(y)) years.add(y);
      }
    });
    return Array.from(years).sort((a, b) => b - a);
  }, [reports]);

  const filteredReports = useMemo(() => {
    const range = getPeriodRange(period, selectedYear);
    return reports.filter(r => r.work_date >= range.start && r.work_date <= range.end);
  }, [reports, period, selectedYear]);

  const periodStats = useMemo(() => {
    if (period === "year") {
      const map = new Map<string, { label: string; count: number; hours: number }>();
      filteredReports.forEach(r => {
        const month = r.work_date ? r.work_date.slice(0, 7) : "";
        if (!month) return;
        if (!map.has(month)) {
          map.set(month, { label: month.slice(5) + "월", count: 0, hours: 0 });
        }
        const entry = map.get(month)!;
        entry.count += 1;
        entry.hours += r.work_hours || 0;
      });
      return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
    }

    const map = new Map<string, { label: string; count: number; hours: number }>();
    filteredReports.forEach(r => {
      const date = r.work_date || "";
      if (!date) return;
      const label = period === "week"
        ? `${new Date(date).getMonth() + 1}/${new Date(date).getDate()}`
        : date.slice(8) + "일";
      if (!map.has(date)) {
        map.set(date, { label, count: 0, hours: 0 });
      }
      const entry = map.get(date)!;
      entry.count += 1;
      entry.hours += r.work_hours || 0;
    });
    return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label));
  }, [filteredReports, period]);

  const chartTitle = period === "week" ? "일별 작업량" : period === "month" ? "일별 작업량" : "월별 작업량 추이";

  const workTypeDistribution = useMemo(() => {
    const map = new Map<string, number>();
    filteredReports.forEach(r => {
      map.set(r.work_type, (map.get(r.work_type) || 0) + 1);
    });
    const total = filteredReports.length || 1;
    return Array.from(map.entries()).map(([type, count]) => ({
      type,
      count,
      percentage: Math.round((count / total) * 1000) / 10,
    }));
  }, [filteredReports]);

  const regionStats = useMemo(() => {
    const map = new Map<string, number>();
    filteredReports.forEach(r => {
      map.set(r.region, (map.get(r.region) || 0) + 1);
    });
    const total = filteredReports.length || 1;
    return Array.from(map.entries())
      .map(([region, count]) => ({
        region,
        count,
        percentage: Math.round((count / total) * 1000) / 10,
      }))
      .sort((a, b) => b.count - a.count);
  }, [filteredReports]);

  const totalReports = filteredReports.length;
  const totalHours = filteredReports.reduce((sum, r) => sum + (r.work_hours || 0), 0);
  const totalWorkers = filteredReports.reduce((sum, r) => sum + (r.worker_count || 0), 0);
  const avgWorkersPerDay = totalReports > 0 ? Math.round(totalWorkers / totalReports) : 0;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <div className="flex flex-col items-center gap-3">
          <div className="w-10 h-10 border-2 border-amber-500 border-t-transparent rounded-full animate-spin"></div>
          <p className="text-sm text-gray-400">통계 불러오는 중...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="w-16 h-16 bg-red-50 rounded-full flex items-center justify-center mb-4">
          <i className="ri-error-warning-line text-red-400 text-2xl"></i>
        </div>
        <p className="text-sm text-gray-600 mb-3">{error}</p>
        <button
          onClick={() => window.location.reload()}
          className="px-4 py-2 bg-amber-500 text-white rounded-lg text-sm font-medium hover:bg-amber-600 cursor-pointer"
        >
          다시 시도
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-xl font-bold text-gray-900">통계</h1>
          <p className="text-sm text-gray-400 mt-0.5">작업 데이터 분석 및 추세 확인</p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => navigate("/statistics/report")}
            className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 cursor-pointer whitespace-nowrap transition-colors"
          >
            <i className="ri-file-chart-line"></i>
            리포트
          </button>
          <div className="flex items-center gap-1 bg-gray-50 rounded-full p-1">
            {([
              { value: "week" as const, label: "주간" },
              { value: "month" as const, label: "월간" },
              { value: "year" as const, label: "연간" }
            ]).map((p) => (
              <button
                key={p.value}
                onClick={() => setPeriod(p.value)}
                className={`px-4 py-1.5 rounded-full text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  period === p.value
                    ? "bg-white text-gray-900"
                    : "text-gray-400 hover:text-gray-600"
                }`}
              >
                {p.label}
              </button>
            ))}
          </div>
          {period === "year" && availableYears.length > 0 && (
            <div className="relative">
              <select
                value={selectedYear}
                onChange={(e) => setSelectedYear(parseInt(e.target.value, 10))}
                className="pl-3 pr-8 py-2 rounded-lg border border-gray-200 text-sm text-gray-700 appearance-none focus:outline-none focus:ring-2 focus:ring-amber-500/20 focus:border-amber-500 cursor-pointer bg-white"
              >
                {availableYears.map(y => (
                  <option key={y} value={y}>{y}년</option>
                ))}
              </select>
              <i className="ri-arrow-down-s-line absolute right-2.5 top-1/2 -translate-y-1/2 text-gray-400 text-sm pointer-events-none"></i>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { label: "총 작업일보", value: totalReports, icon: "ri-file-text-line", unit: "건", color: "bg-amber-50 text-amber-600" },
          { label: "총 작업 시간", value: totalHours.toLocaleString(), icon: "ri-time-line", unit: "시간", color: "bg-emerald-50 text-emerald-600" },
          { label: "일평균 인원", value: avgWorkersPerDay, icon: "ri-team-line", unit: "명/일", color: "bg-rose-50 text-rose-600" },
          { label: "총 투입 인원", value: totalWorkers.toLocaleString(), icon: "ri-user-line", unit: "명", color: "bg-indigo-50 text-indigo-600" }
        ].map((card) => (
          <div key={card.label} className="bg-white rounded-xl p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="text-sm text-gray-400">{card.label}</span>
              <div className={`w-9 h-9 rounded-lg flex items-center justify-center ${card.color}`}>
                <i className={`${card.icon} text-sm`}></i>
              </div>
            </div>
            <div className="flex items-baseline gap-1">
              <span className="text-2xl font-bold text-gray-900">{card.value}</span>
              <span className="text-sm text-gray-400">{card.unit}</span>
            </div>
          </div>
        ))}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-5">{chartTitle}</h2>
          {periodStats.length === 0 ? (
            <div className="flex items-center justify-center h-[280px]">
              <p className="text-sm text-gray-400">데이터가 부족합니다</p>
            </div>
          ) : (
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={periodStats}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="label" tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={{ stroke: "#e2e8f0" }} tickLine={false} />
                <YAxis tick={{ fontSize: 12, fill: "#94a3b8" }} axisLine={{ stroke: "#e2e8f0" }} tickLine={false} />
                <Tooltip
                  contentStyle={{
                    borderRadius: "12px",
                    border: "none",
                    boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                    fontSize: "13px"
                  }}
                />
                <Legend />
                <Bar dataKey="count" name="일보 건수" fill="#f59e0b" radius={[4, 4, 0, 0]} />
                <Bar dataKey="hours" name="작업 시간" fill="#10b981" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="bg-white rounded-xl p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-5">작업 유형별 분포</h2>
          {workTypeDistribution.length === 0 ? (
            <div className="flex items-center justify-center h-[280px]">
              <p className="text-sm text-gray-400">데이터가 부족합니다</p>
            </div>
          ) : (
            <div className="flex items-center">
              <ResponsiveContainer width="55%" height={280}>
                <PieChart>
                  <Pie
                    data={workTypeDistribution}
                    cx="50%"
                    cy="50%"
                    innerRadius={55}
                    outerRadius={95}
                    paddingAngle={3}
                    dataKey="count"
                    nameKey="type"
                  >
                    {workTypeDistribution.map((_, index) => (
                      <Cell key={index} fill={PIE_COLORS[index % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip
                    contentStyle={{
                      borderRadius: "12px",
                      border: "none",
                      boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                      fontSize: "13px"
                    }}
                    formatter={(value: number) => [`${value}건`, ""]}
                  />
                </PieChart>
              </ResponsiveContainer>
              <div className="flex-1 space-y-2">
                {workTypeDistribution.slice(0, 6).map((item, index) => (
                  <div key={item.type} className="flex items-center gap-2.5">
                    <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: PIE_COLORS[index] }}></div>
                    <span className="text-sm text-gray-600 min-w-[48px]">{item.type}</span>
                    <span className="text-xs text-gray-400 ml-auto">{item.percentage}%</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="bg-white rounded-xl p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-5">지역별 통계</h2>
          {regionStats.length === 0 ? (
            <div className="flex items-center justify-center h-[280px]">
              <p className="text-sm text-gray-400">데이터가 부족합니다</p>
            </div>
          ) : (
            <div className="space-y-4">
              {regionStats.map((item, index) => (
                <div key={item.region}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-sm font-medium text-gray-700">{item.region}</span>
                    <span className="text-sm text-gray-400">{item.count}건 ({item.percentage}%)</span>
                  </div>
                  <div className="w-full h-2.5 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all"
                      style={{
                        width: `${Math.max(item.percentage, 3)}%`,
                        backgroundColor: CHART_COLORS[index % CHART_COLORS.length]
                      }}
                    ></div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        <div className="bg-white rounded-xl p-6">
          <h2 className="text-base font-semibold text-gray-900 mb-5">통계 요약</h2>
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-xs text-gray-400 mb-1">총 작업 유형</p>
              <p className="text-xl font-bold text-gray-900">{workTypeDistribution.length}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-xs text-gray-400 mb-1">총 지역</p>
              <p className="text-xl font-bold text-gray-900">{regionStats.length}</p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-xs text-gray-400 mb-1">평균 인원</p>
              <p className="text-xl font-bold text-gray-900">{avgWorkersPerDay}<span className="text-sm font-normal text-gray-400">명</span></p>
            </div>
            <div className="bg-gray-50 rounded-lg p-4">
              <p className="text-xs text-gray-400 mb-1">평균 시간</p>
              <p className="text-xl font-bold text-gray-900">
                {totalReports > 0 ? (totalHours / totalReports).toFixed(1) : 0}
                <span className="text-sm font-normal text-gray-400">h</span>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}